/**
 * ManageCustomers Page
 * 
 * ARCHITECTURE (Jan 2026):
 * Calendar-scoped customer management page with sidebar navigation.
 * Replaces the customer portion of ManageUsers for multi-calendar support.
 * 
 * Features:
 * - Sidebar showing all calendars owned by the admin
 * - Customer list scoped to the selected calendar
 * - Add/Edit/Delete customers within calendar context
 * - Message console integration for customer communication
 * 
 * API Endpoints Used:
 * - GET /api/calendars - Fetch admin's calendars (used by sidebar)
 * - GET /api/calendars/{calendar_id}/customers - Fetch customers for calendar
 * - POST /api/calendars/{calendar_id}/customers - Create customer
 * - PUT /api/calendars/{calendar_id}/customers/{id} - Update customer
 * - DELETE /api/calendars/{calendar_id}/customers/{id} - Delete customer
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CalendarSidebar from '../components/CalendarSidebar';
import MessageConsole from '../components/MessageConsole';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, Edit, Trash2, X, User, RefreshCw, Search, 
  MessageSquare, Mail, Phone, Calendar, Users, UserCog, ArrowLeft 
} from 'lucide-react';
import PhoneInput, { formatStoredPhone } from '../components/PhoneInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManageCustomers = () => {
  const { token, isAdmin, isCalendarAdmin, isSuperAdmin, getAuthHeaders, runningUser, canRunAs, startRunAs } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get calendar from URL or default to null (sidebar will auto-select first)
  const initialCalendarId = searchParams.get('calendar') || null;
  
  // State for selected calendar and customers
  const [selectedCalendarId, setSelectedCalendarId] = useState(initialCalendarId);
  const [selectedCalendarName, setSelectedCalendarName] = useState('');
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // SINGLE CALENDAR OPTIMIZATION (Jan 2026):
  // Track calendar count to hide sidebar when admin has only one calendar
  const [calendarCount, setCalendarCount] = useState(0);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Message console state
  const [showMessageConsole, setShowMessageConsole] = useState(false);
  const [selectedCustomerForMessage, setSelectedCustomerForMessage] = useState(null);
  
  // Search filter
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form data for add/edit modal
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    accepts_sms: false
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /**
   * Handle calendar selection from sidebar.
   * Updates URL param for shareability/bookmarking.
   */
  const handleCalendarSelect = useCallback((calendarId) => {
    setSelectedCalendarId(calendarId);
    setSearchParams({ calendar: calendarId });
  }, [setSearchParams]);

  /**
   * Callback when CalendarSidebar loads calendars.
   * SINGLE CALENDAR OPTIMIZATION (Jan 2026):
   * Used to track calendar count and store calendar name for header display.
   */
  const handleCalendarsLoaded = useCallback((count, calendars) => {
    setCalendarCount(count);
    // If single calendar, store its name for display in header
    if (count === 1 && calendars.length > 0) {
      setSelectedCalendarName(calendars[0].name);
    }
  }, []);

  /**
   * Fetch customers for the selected calendar.
   * Uses the new calendar-scoped endpoint.
   */
  const fetchCustomers = useCallback(async () => {
    if (!selectedCalendarId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/calendars/${selectedCalendarId}/customers`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setCustomers(response.data.data || []);
        setSelectedCalendarName(response.data.calendar?.name || '');
        setError('');
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [selectedCalendarId, getAuthHeaders]);

  // Fetch customers when calendar changes
  useEffect(() => {
    if (selectedCalendarId) {
      fetchCustomers();
    }
  }, [selectedCalendarId, fetchCustomers]);

  // Filter customers based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCustomers(customers);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = customers.filter(customer => 
      customer.full_name?.toLowerCase().includes(query) ||
      customer.email?.toLowerCase().includes(query) ||
      customer.phone?.includes(query)
    );
    setFilteredCustomers(filtered);
  }, [customers, searchQuery]);

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/');
    }
  }, [isAdmin, navigate]);

  /**
   * Handle refresh button click.
   */
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCustomers();
    setRefreshing(false);
  };

  /**
   * Open add customer modal.
   */
  const openAddModal = () => {
    setEditingCustomer(null);
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      password: '',
      accepts_sms: false
    });
    setFormError('');
    setShowModal(true);
  };

  /**
   * Open edit customer modal.
   */
  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      full_name: customer.full_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      password: '', // Don't show existing password
      accepts_sms: customer.accepts_sms || false
    });
    setFormError('');
    setShowModal(true);
  };

  /**
   * Handle form submission for add/edit customer.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    
    try {
      if (editingCustomer) {
        // Update existing customer
        const updateData = {
          full_name: formData.full_name,
          phone: formData.phone || null,
          accepts_sms: formData.accepts_sms
        };
        
        await axios.put(
          `${BACKEND_URL}/api/calendars/${selectedCalendarId}/customers/${editingCustomer.id}`,
          updateData,
          { headers: getAuthHeaders() }
        );
        
        setSuccess('Customer updated successfully');
      } else {
        // Create new customer
        if (!formData.password || formData.password.length < 6) {
          setFormError('Password must be at least 6 characters');
          setSubmitting(false);
          return;
        }
        
        await axios.post(
          `${BACKEND_URL}/api/calendars/${selectedCalendarId}/customers`,
          formData,
          { headers: getAuthHeaders() }
        );
        
        setSuccess('Customer created successfully');
      }
      
      setShowModal(false);
      fetchCustomers();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Operation failed';
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle delete customer.
   */
  const handleDelete = async (customerId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/calendars/${selectedCalendarId}/customers/${customerId}`,
        { headers: getAuthHeaders() }
      );
      
      setSuccess('Customer deleted successfully');
      setShowDeleteConfirm(null);
      fetchCustomers();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete customer');
    }
  };

  /**
   * Handle "Run As" to impersonate a customer.
   * Allows admin to see the app from customer's perspective.
   */
  const handleRunAs = (customer) => {
    if (!canRunAs(runningUser, customer)) {
      console.error('Cannot run as this customer');
      return;
    }
    
    startRunAs({
      id: customer.id,
      role: customer.role || 'customer',
      full_name: customer.full_name,
      email: customer.email
    }, window.location.pathname);
    
    // Navigate to customer dashboard
    window.location.href = '/dashboard';
  };

  /**
   * Open message console for a customer.
   */
  const openMessageConsole = (customer) => {
    setSelectedCustomerForMessage(customer);
    setShowMessageConsole(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="flex-1 flex flex-col lg:flex-row">
        {/* Calendar Sidebar - Hidden if only one calendar (SINGLE CALENDAR OPTIMIZATION Jan 2026) */}
        <CalendarSidebar
          selectedCalendarId={selectedCalendarId}
          onCalendarSelect={handleCalendarSelect}
          onCalendarsLoaded={handleCalendarsLoaded}
          className="flex-shrink-0"
        />
        
        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-6 overflow-auto">
          {/* Back to Dashboard */}
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Manage Customers
                </h1>
                {/* Show calendar name - especially important when sidebar is hidden (single calendar) */}
                {selectedCalendarName && (
                  <p className="text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {selectedCalendarName}
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing || !selectedCalendarId}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
                  title="Refresh"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                
                <button
                  onClick={openAddModal}
                  disabled={!selectedCalendarId}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="add-customer-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add Customer</span>
                </button>
              </div>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError('')} className="p-1 hover:bg-red-200 dark:hover:bg-red-800 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg flex items-center justify-between">
              <span>{success}</span>
              <button onClick={() => setSuccess('')} className="p-1 hover:bg-green-200 dark:hover:bg-green-800 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Search Bar */}
          {selectedCalendarId && (
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="search-input"
                />
              </div>
            </div>
          )}

          {/* Content */}
          {!selectedCalendarId ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Select a calendar from the sidebar to manage customers
              </p>
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Loading customers...</p>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery ? 'No customers match your search' : 'No customers yet'}
              </p>
              {!searchQuery && (
                <button
                  onClick={openAddModal}
                  className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Add your first customer
                </button>
              )}
            </div>
          ) : (
            /* Customer Table */
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                        Contact
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                        Appointments
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredCustomers.map((customer) => (
                      <tr 
                        key={customer.id} 
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        data-testid={`customer-row-${customer.id}`}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 dark:text-gray-200">
                                {customer.full_name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 sm:hidden">
                                {customer.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 hidden sm:table-cell">
                          <div className="space-y-1">
                            <p className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                              <Mail className="w-3.5 h-3.5" />
                              {customer.email}
                            </p>
                            {customer.phone && (
                              <p className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                                <Phone className="w-3.5 h-3.5" />
                                {formatStoredPhone(customer.phone)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-gray-600 dark:text-gray-300">
                            {customer.appointment_count || 0} this year
                          </span>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                            customer.is_active !== false
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          }`}>
                            {customer.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openMessageConsole(customer)}
                              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg"
                              title="Message"
                              data-testid={`message-btn-${customer.id}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                            {/* Run As button - allows admin to see app as this customer */}
                            {canRunAs && canRunAs(runningUser, customer) && (
                              <button
                                onClick={() => handleRunAs(customer)}
                                className="p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg"
                                title={`Run as ${customer.full_name}`}
                                data-testid={`run-as-btn-${customer.id}`}
                              >
                                <UserCog className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openEditModal(customer)}
                              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                              title="Edit"
                              data-testid={`edit-btn-${customer.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(customer)}
                              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                              title="Delete"
                              data-testid={`delete-btn-${customer.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                {editingCustomer ? 'Edit Customer' : 'Add Customer'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Show which calendar the customer will be added to */}
              {selectedCalendarName && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    {editingCustomer ? 'Customer of' : 'Adding to'}: <strong>{selectedCalendarName}</strong>
                  </span>
                </div>
              )}
              
              {formError && (
                <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm">
                  {formError}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="input-full-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  disabled={!!editingCustomer}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="input-email"
                />
                {editingCustomer && (
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(value) => setFormData(prev => ({ ...prev, phone: value }))}
                  className="w-full"
                  data-testid="input-phone"
                />
              </div>
              
              {!editingCustomer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required={!editingCustomer}
                    minLength={6}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="input-password"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="accepts_sms"
                  checked={formData.accepts_sms}
                  onChange={(e) => setFormData(prev => ({ ...prev, accepts_sms: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid="checkbox-sms"
                />
                <label htmlFor="accepts_sms" className="text-sm text-gray-700 dark:text-gray-300">
                  Customer accepts SMS notifications
                </label>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="submit-btn"
                >
                  {submitting ? 'Saving...' : editingCustomer ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
              Delete Customer
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to delete <strong>{showDeleteConfirm.full_name}</strong>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Console */}
      {showMessageConsole && selectedCustomerForMessage && (
        <MessageConsole
          isOpen={showMessageConsole}
          onClose={() => {
            setShowMessageConsole(false);
            setSelectedCustomerForMessage(null);
          }}
          customer={selectedCustomerForMessage}
        />
      )}

      <Footer />
    </div>
  );
};

export default ManageCustomers;
